import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { Bookmark, Globe, Loader2 } from "lucide-react";

const ACCENT_COLORS = ["#3E7C6A", "#7A5C3B", "#A64F5C", "#4B4B7A", "#2E7BA6", "#8A6BAE"];

function accentFor(f) {
  if (f.color) return f.color;
  const seed = (f.id || "0").split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return ACCENT_COLORS[seed % ACCENT_COLORS.length];
}

export default function Collections() {
  const { t } = useI18n();
  const navigate = useNavigate();
  usePageMeta(t("collections_title"));
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bookmarks/folders");
      setFolders(Array.isArray(data) ? data : []);
    } catch { setFolders([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="mx-auto max-w-3xl space-y-4" data-testid="collections-index">
      <div>
        <h1 className="font-display text-3xl text-foreground">{t("collections_title")}</h1>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : folders.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-16 text-center">
          <Bookmark className="h-10 w-10 text-muted-foreground/50" />
          <p className="font-display text-lg text-foreground">{t("collections_empty_title")}</p>
          <p className="max-w-sm text-sm text-muted-foreground">{t("collections_empty_sub")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {folders.map((f) => {
            const accent = accentFor(f);
            return (
              <button
                key={f.id}
                data-testid={`collection-card-${f.id}`}
                onClick={() => navigate(`/app/collections/${f.id}`)}
                className="overflow-hidden rounded-2xl border bg-card text-left transition-transform hover:-translate-y-0.5"
                style={{ borderColor: accent + "44" }}
              >
                <div className="relative flex aspect-[1.1/1] flex-col justify-end p-2.5" style={{ backgroundColor: accent }}>
                  <span className="absolute left-3 top-3 text-3xl">{f.emoji || "🌱"}</span>
                  <div className="flex items-end justify-between gap-2">
                    <span className="text-xs font-medium text-white drop-shadow">{t("collections_n_posts", { n: f.count })}</span>
                    {f.is_public ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground">
                        <Globe className="h-2.5 w-2.5" /> {t("collections_public")}
                      </span>
                    ) : null}
                  </div>
                </div>
                <p className="truncate px-2.5 pt-2.5 text-sm font-semibold text-foreground">{f.name}</p>
                {f.description ? <p className="line-clamp-2 px-2.5 pb-2.5 text-xs text-muted-foreground">{f.description}</p> : <div className="pb-2.5" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
